import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../../../components/ConfirmDialog';
import StyleConfigModal from './StyleConfigModal';
import { useProjectConfigState } from '../hooks/useProjectConfigState';

type ConfigState = ReturnType<typeof useProjectConfigState>;

interface StyleConfigDialogProps {
  cfg: ConfigState;
  projectId?: string;
  open: boolean;
  onClose: () => void;
  /** false = 硬强制模式：隐藏 ✕/取消、禁用 Esc（新建项目必须完成风格的 Step3）。默认 true。 */
  dismissible?: boolean;
}

/**
 * 自包含「风格设置」弹窗：渲染现有 StyleConfigModal + 应用模板确认。
 * 配置态由父级持有的 useProjectConfigState(cfg) 提供，行为与迁移前一致：
 * - 关闭时 resetFromProject() 丢弃未保存改动（原 onClose 行为）。
 * - 保存走 cfg.save('style', tpl)（原 handleSaveProject('style')）。
 * - 应用模板确认走 cfg.applyPendingTemplate（原 ProjectConfirmDialogs 的批量回填）。
 */
export default function StyleConfigDialog({ cfg, projectId, open, onClose, dismissible = true }: StyleConfigDialogProps) {
  const { t } = useTranslation('projectDetail');
  return (
    <>
      <StyleConfigModal
        {...cfg}
        open={open}
        dismissible={dismissible}
        projectId={projectId}
        t={t}
        generationTemplates={cfg.bootstrap.generationTemplates}
        activeImageModels={cfg.bootstrap.activeImageModels}
        activeVideoModels={cfg.bootstrap.activeVideoModels}
        llmProviders={cfg.bootstrap.llmProviders}
        selectedTemplateId={cfg.editGenerationTemplateId}
        onClose={() => { cfg.resetFromProject(); onClose(); }}
        onSave={async (templateToApply) => { if (await cfg.save('style', templateToApply)) onClose(); }}
      />
      <ConfirmDialog
        open={!!cfg.pendingTemplate}
        onCancel={() => cfg.setPendingTemplate(null)}
        onConfirm={cfg.applyPendingTemplate}
        title={t('confirmDialogs.applyPreset.title')}
        message={<>{t('confirmDialogs.applyPreset.title')}: <strong>{cfg.pendingTemplate?.name}</strong></>}
        confirmText={t('confirmDialogs.applyPreset.confirm')}
      />
    </>
  );
}
